//! Formula helpers for the custom cell editor: grid cell-reference highlights,
//! point-and-click reference detection, F4 absolute/relative toggling, and
//! parenthesis balancing. These reuse the same quadratic-core WASM services and
//! Pixi highlight infrastructure as the Monaco-based inline editor.

import { sheets } from '@/app/grid/controller/Sheets';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import {
  checkFormula,
  parseFormula,
  toggleReferenceTypes,
  wantsCellRefAtCaret as wantsCellRefAtCaretWasm,
} from '@/app/quadratic-core/quadratic_core';
import type { SheetPosTS } from '@/app/shared/types/size';

/// Returns whether the caret is in a position that expects a cell reference next
/// (used for point-and-click insertion and formula arrow-key navigation).
///
/// Formula editing uses contextual arrow mode: arrows move the grid cursor to
/// pick a cell reference only when the caret is where a new reference would be
/// typed (e.g. `=|`, `=SUM(|`, `=A1+|`). Otherwise arrows move within the text.
///
/// The determination is delegated to quadratic-core's formula lexer (a
/// synchronous, context-free WASM call) so it stays in sync with how formulas
/// are actually tokenized.
export const wantsCellRefAtCaret = (value: string, caret: number): boolean => wantsCellRefAtCaretWasm(value, caret);

/// Updates the grid's cell-reference highlights for the given formula value.
/// `value` includes the leading `=`.
export const updateFormulaHighlights = (value: string, location: SheetPosTS): void => {
  try {
    const parsed = parseFormula(value.slice(1), sheets.jsA1Context, location.sheetId, location.x, location.y);
    content.cellHighlights.fromCellsAccessed(parsed.cells_accessed, false);
  } catch (_e) {
    content.cellHighlights.clear();
  }
};

export const clearFormulaHighlights = (): void => {
  content.cellHighlights.clear();
};

/// Returns true if the formula (without the leading `=`) parses successfully.
export const isFormulaValid = (value: string, location: SheetPosTS): boolean => {
  try {
    return checkFormula(value.slice(1), sheets.jsA1Context, location.sheetId, location.x, location.y);
  } catch (_e) {
    return false;
  }
};

/// Appends any missing closing parentheses, returning the balanced value.
export const closeParentheses = (value: string): string => {
  let count = 0;
  for (const ch of value) {
    if (ch === '(') count++;
    else if (ch === ')') count--;
  }
  let result = value;
  for (let i = 0; i < count; i++) result += ')';
  return result;
};

export interface CellReferenceMatch {
  text: string;
  start: number;
  end: number;
}

/// Finds a cell-reference-like token containing the caret position.
export const referenceAtCaret = (value: string, caret: number): CellReferenceMatch | undefined => {
  const referenceRegex = /[$A-Za-z0-9']+/g;
  let match: RegExpExecArray | null;
  const cursorIndex = Math.max(0, caret - 1);
  while ((match = referenceRegex.exec(value)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (cursorIndex >= start && cursorIndex < end) {
      return { text: match[0], start, end };
    }
  }
  return undefined;
};

/// Toggles the absolute/relative ($) markers of the reference at the caret
/// (F4 behavior). Returns the new value and caret, or undefined if there is no
/// valid reference at the caret.
export const toggleReferenceAtCaret = (value: string, caret: number): { value: string; caret: number } | undefined => {
  const reference = referenceAtCaret(value, caret);
  if (!reference) return undefined;
  try {
    const newReference = toggleReferenceTypes(reference.text);
    const newValue = value.slice(0, reference.start) + newReference + value.slice(reference.end);
    return { value: newValue, caret: reference.start + newReference.length };
  } catch (_e) {
    return undefined;
  }
};
